import { invalidateWiki, wikiKeys } from '../shared/useWikiQueries';

/**
 * Cache updates after a create, so the destination the modal navigates to
 * renders at once (TopBar crumb, nav row, category header) instead of
 * flashing "not found" until the background refetch lands.
 *
 * Every create/commit invalidates `categories`, `home`, `counts` and `pages`
 * (FE-nav-home-ack request) so the nav counts and Home cards update at once.
 */
export const WIKI_CREATE_SCOPES = Object.freeze(['categories', 'category', 'home', 'counts', 'pages']);

/**
 * Append a created category (minus GET-by-id extras) to the cached
 * `GET /wiki/categories`, and seed its `['wiki','category',id]` entry with the
 * full POST response, which carries `access_summary` (#871): the new page
 * shows its access line at once, with no extra request.
 */
export function primeCreatedCategory(queryClient, created) {
    if (!queryClient || !created || !created.category_id) return Promise.resolve();
    queryClient.setQueryData(wikiKeys.category(created.category_id), created);
    const { parent, subcategories, access_summary: accessSummary, ...row } = created;
    queryClient.setQueryData(wikiKeys.categories(), (old) => {
        if (!old) return old;
        const rows = Array.isArray(old) ? old : old.categories;
        if (!Array.isArray(rows)) return old;
        if (rows.some((item) => (item.category_id || item.id) === row.category_id)) return old;
        const next = [...rows, row];
        return Array.isArray(old) ? next : { ...old, categories: next };
    });
    return invalidateWiki(queryClient, ...WIKI_CREATE_SCOPES);
}

/** Seed `['wiki','page',id]` with the created page and refresh the lists that count it. */
export function primeCreatedPage(queryClient, page, { requiredAck = false } = {}) {
    if (!queryClient || !page || !page.page_id) return Promise.resolve();
    queryClient.setQueryData(wikiKeys.page(page.page_id), page);
    const scopes = [...WIKI_CREATE_SCOPES];
    if (requiredAck) scopes.push('ack');
    return invalidateWiki(queryClient, ...scopes);
}
